import React from 'react';
import { useReactFlow, useViewport, type Edge, type Node } from '@xyflow/react';
import { getHandleColor } from '../../utils/handleColors';
import type { RuleNodeData } from '../RuleNode';
import type { HandleType } from '../../types/registry';
import classes from './EdgeTooltip.module.css';

export type EdgeTooltipProps = {
  /** The currently selected edges. */
  selectedEdges: Edge[];
  /** Callback when the optional flag is toggled on an edge. */
  onToggleOptional?: ( edgeId: string, optional: boolean ) => void;
  /** Whether editing is allowed. */
  readOnly?: boolean;
};

/**
 * Resolve source/target handle names and types for display.
 */
function useEdgeHandleInfo( edge: Edge | undefined ) {
  const { getNode } = useReactFlow();

  if ( !edge ) {
    return {
      sourceHandleName: 'output',
      targetHandleName: 'input',
      sourceType: 'any' as HandleType,
      targetType: 'any' as HandleType
    };
  }

  const sourceNode = getNode( edge.source ) as unknown as { data: RuleNodeData } | undefined;
  const targetNode = getNode( edge.target ) as unknown as { data: RuleNodeData } | undefined;

  const sourceHandle = sourceNode?.data.outputs?.find(( h ) => h.name === edge.sourceHandle );
  const targetHandle = targetNode?.data.inputs?.find(( h ) => h.name === edge.targetHandle );

  return {
    sourceHandleName: edge.sourceHandle || 'output',
    targetHandleName: edge.targetHandle || 'input',
    sourceType: ( sourceHandle?.type || 'any' ) as HandleType,
    targetType: ( targetHandle?.type || 'any' ) as HandleType
  };
}

/**
 * Compute the container-relative position for the tooltip near the edge midpoint.
 * Converts flow coordinates to screen-relative coordinates using the viewport transform.
 */
function useEdgeContainerPosition( edge: Edge | undefined ): { x: number; y: number } | null {
  const { getNode } = useReactFlow();
  const viewport = useViewport();

  if ( !edge ) return null;

  const sourceNode = getNode( edge.source ) as Node | undefined;
  const targetNode = getNode( edge.target ) as Node | undefined;

  if ( !sourceNode || !targetNode ) return null;

  const sourceWidth = ( sourceNode.style?.width as number ) || sourceNode.measured?.width || 230;
  const sourceHeight = ( sourceNode.style?.height as number ) || sourceNode.measured?.height || 80;
  const targetHeight = ( targetNode.style?.height as number ) || targetNode.measured?.height || 80;

  // Source handle is on the right, target handle is on the left
  const sx = sourceNode.position.x + sourceWidth;
  const sy = sourceNode.position.y + sourceHeight / 2;
  const tx = targetNode.position.x;
  const ty = targetNode.position.y + targetHeight / 2;

  const flowMidX = ( sx + tx ) / 2;
  const flowMidY = ( sy + ty ) / 2;

  // Convert flow coordinates to container-relative coordinates
  // using the viewport transform: screenX = flowX * zoom + panX
  return {
    x: flowMidX * viewport.zoom + viewport.x,
    y: flowMidY * viewport.zoom + viewport.y
  };
}

/**
 * Floating tooltip that appears near selected edges, showing
 * source handle -> target handle names and an optional toggle.
 *
 * Renders as an absolutely positioned overlay inside the flow container.
 * Must be placed as a sibling of <ReactFlow> within a position: relative wrapper.
 */
export function EdgeTooltip({ selectedEdges, onToggleOptional, readOnly }: EdgeTooltipProps ) {
  // Always call hooks unconditionally
  const edge = selectedEdges.length > 0 ? selectedEdges[0] : undefined;
  const { sourceHandleName, targetHandleName, sourceType, targetType } = useEdgeHandleInfo( edge );
  const position = useEdgeContainerPosition( edge );

  if ( !edge || !position ) return null;

  const isOptional = !!edge.data?.optional;

  return (
    <div
      className={classes.tooltip}
      style={{ left: position.x, top: position.y - 10 }}
    >
      <div className={classes.connectionLabel}>
        <span
          className={classes.handleName}
          style={{ color: getHandleColor( sourceType ) }}
        >
          {sourceHandleName}
        </span>
        <span className={classes.arrow}>&rarr;</span>
        <span
          className={classes.handleName}
          style={{ color: getHandleColor( targetType ) }}
        >
          {targetHandleName}
        </span>
      </div>
      <div className={classes.optionalRow}>
        <input
          type="checkbox"
          className={classes.checkbox}
          checked={isOptional}
          disabled={readOnly}
          onChange={( e ) => onToggleOptional?.( edge.id, e.target.checked )}
        />
        <span>Optional</span>
      </div>
    </div>
  );
}
